import type { AlertRootProps } from "@kobalte/core/alert";
import { Alert as AlertPrimitive } from "@kobalte/core/alert";
import type { PolymorphicProps } from "@kobalte/core/polymorphic";
import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import type { ComponentProps, ValidComponent } from "solid-js";
import { splitProps } from "solid-js";
import { cn } from "@/libs/cn";

export const alertVariants = cva(
  "relative w-full rounded-lg border px-4 py-3 text-sm [&:has(svg)]:pl-11 [&>svg+div]:translate-y-[-1px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-3 [&>svg]:text-foreground",
  {
    variants: {
      variant: {
        default: "bg-background text-foreground",
        destructive:
          "border-destructive/50 bg-destructive/10 text-destructive dark:border-destructive [&>svg]:text-destructive",
        info: "bg-blue-200/50 text-blue-500 border-blue-500 [&>svg]:text-blue-500 dark:[&>svg]:border-blue-500",
        success:
          "bg-green-200/50 border-green-500 text-green-500 [&>svg]:text-green-500 dark:[&>svg]:border-green-500",
        warning:
          "bg-yellow-100/50 border-yellow-500 text-yellow-600 [&>svg]:text-yellow-600 dark:[&>svg]:border-yellow-500",
        warning_outline:
          "border border-yellow-500 text-yellow-600 hover:bg-yellow-500/10 hover:text-yellow-600",
        info_outline:
          "border border-blue-500 text-blue-500 hover:bg-blue-500/10 hover:text-blue-500",
        success_outline:
          "border border-green-500 text-green-500 hover:bg-green-500/10 hover:text-green-500",
        destructive_outline:
          "border border-destructive/50 text-destructive dark:border-destructive hover:bg-destructive/10 hover:text-destructive",
        outline:
          "border border-input shadow-xs hover:bg-accent bg-white text-primary",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

type alertProps<T extends ValidComponent = "div"> = AlertRootProps<T> &
  VariantProps<typeof alertVariants> & {
    class?: string;
  };

export const Alert = <T extends ValidComponent = "div">(
  props: PolymorphicProps<T, alertProps<T>>,
) => {
  const [local, rest] = splitProps(props as alertProps, ["class", "variant"]);

  return (
    <AlertPrimitive
      class={cn(
        alertVariants({
          variant: props.variant,
        }),
        local.class,
      )}
      {...rest}
    />
  );
};

export const AlertTitle = (props: ComponentProps<"div">) => {
  const [local, rest] = splitProps(props, ["class"]);

  return (
    <div
      class={cn(
        "font-display font-medium text-base leading-5 tracking-tight",
        local.class,
      )}
      {...rest}
    />
  );
};

export const AlertDescription = (props: ComponentProps<"div">) => {
  const [local, rest] = splitProps(props, ["class"]);

  return (
    <div class={cn("text-sm [&_p]:leading-relaxed", local.class)} {...rest} />
  );
};

export const AlertAction = (props: ComponentProps<"div">) => {
  const [local, rest] = splitProps(props, ["class"]);

  return (
    <div
      class={cn(
        "text-sm [&_p]:leading-relaxed absolute right-4 top-4",
        local.class,
      )}
      {...rest}
    />
  );
};
